import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import {
  fetchMaintenanceTypes,
  fetchMaintenanceStatuses,
  fetchMaintenanceReports,
  fetchPlotMaintenanceHistory,
} from "../api/api";
import { usePlots } from "../context/PlotContext";

const CHECKLIST_ITEMS = [
  { key: "clearing_approach_road", label: "Clearing Approach Road" },
  { key: "deweeding_plot_cleaning", label: "Deweeding and Plot Cleaning" },
  { key: "basin_cleaning_preparation", label: "Basin Cleaning and Preparation" },
  { key: "checking_drip_lines", label: "Checking/Replacing/Re-fixing Drip Lines" },
  { key: "pruning_plants_trees", label: "Pruning Plants/Trees" },
];

// Read-only view of what the customer's plot is currently subscribed to —
// same scope as the mobile app's MaintenanceScreen. There's still no
// customer-facing endpoint to request/change a plan, so this stays a
// display screen with a prompt to contact support.
function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function formatCurrency(amount) {
  if (amount === null || amount === undefined || amount === "") return null;
  const num = Number(amount);
  if (Number.isNaN(num)) return null;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(num);
}

function errorMessage(err) {
  return err.response?.data?.detail || "Could not load your maintenance details. Please try again.";
}

function CustomerMaintenance() {
  const navigate = useNavigate();

  const { plots, selectedPlot: plot, selectedPlotId, selectPlot, loading: plotsLoading } = usePlots();
  const [typeName, setTypeName] = useState(null);
  const [statusName, setStatusName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const detailsRef = useRef(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [types, statuses] = await Promise.all([
        fetchMaintenanceTypes().catch(() => []),
        fetchMaintenanceStatuses().catch(() => []),
      ]);
      setTypeName(types.find((t) => t.id === plot?.maintenance_type_id)?.name || null);
      setStatusName(statuses.find((s) => s.id === plot?.maintenance_status_id)?.name || null);

      if (plot?.id) {
        setReportsLoading(true);
        setReportsError("");
        try {
          const data = await fetchMaintenanceReports(plot.id);
          setReports(data);
        } catch (err) {
          setReportsError("Could not load your maintenance reports.");
        } finally {
          setReportsLoading(false);
        }

        setHistoryLoading(true);
        try {
          const historyData = await fetchPlotMaintenanceHistory(plot.id);
          setHistory(historyData);
        } catch (err) {
          setHistory([]);
        } finally {
          setHistoryLoading(false);
        }
      } else {
        setReports([]);
        setHistory([]);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [plot]);

  useEffect(() => { if (!plotsLoading) load(); }, [load, plotsLoading]);

  useEffect(() => {
    if (selectedReport && detailsRef.current) {
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedReport]);

  // Subscribed if either the master-linked type or the older free-text
  // field is set — a plot can carry either depending on when it was set up.
  const planLabel = typeName || plot?.maintenance_type;
  const subscribed = !!planLabel;
  const dueAmount = formatCurrency(plot?.maintenance_due_amount);

  return (
    <>
      <Navbar />
      <Sidebar />

      <div className="main-content">
        <div className="myfarm-container">

          <h2 className="welcome-title">Farm Maintenance</h2>

          {(loading || plotsLoading) && <p>Loading your maintenance details…</p>}
          {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}

          {!loading && !plotsLoading && plots.length > 1 && (
            <div style={{ marginBottom: "10px" }}>
              <label style={{ fontWeight: 600, marginRight: "8px" }}>Plot</label>
              <select value={selectedPlotId || ""} onChange={(e) => selectPlot(Number(e.target.value))}>
                {plots.map((p) => <option key={p.id} value={p.id}>{p.plot_number}</option>)}
              </select>
            </div>
          )}

          {!loading && !plotsLoading && !error && (
            <div className="farm-card">
              {subscribed ? (
                <>
                  <h3>{planLabel}</h3>

                  {statusName && (
                    <div className="farm-row">
                      <span>Status</span>
                      <strong>{statusName}</strong>
                    </div>
                  )}

                  {/* {plot.maintenance_unit_price != null && (
                    <div className="farm-row">
                      <span>Rate</span>
                      <strong>₹{plot.maintenance_unit_price}/sq ft</strong>
                    </div>
                  )} */}

                  {formatDate(plot.last_maintenance_date) && (
                    <div className="farm-row">
                      <span>Last Serviced</span>
                      <strong>{formatDate(plot.last_maintenance_date)}</strong>
                    </div>
                  )}

                  {formatDate(plot.maintenance_validity_date) && (
                    <div className="farm-row">
                      <span>Valid Until</span>
                      <strong>{formatDate(plot.maintenance_validity_date)}</strong>
                    </div>
                  )}

                  {plot.maintenance_due_this_month && (
                    <p className="due-date" style={{ color: "#dc2626" }}>
                      💰 Maintenance is due this month{dueAmount ? ` — ${dueAmount}` : ""}. Check Billing to pay.
                    </p>
                  )}

                  <p style={{ color: "#666", fontSize: "14px", marginTop: "12px" }}>
                    To change your plan or ask about your maintenance schedule, please contact our support team.
                  </p>

                  <button className="register-btn" onClick={() => navigate("/contact")}>
                    📞 Contact Support
                  </button>
                </>
              ) : (
                <>
                  <h3>No maintenance plan yet</h3>
                  <p style={{ color: "#666", fontSize: "14px" }}>
                    You have not subscribed to a Farm Maintenance plan for your plot. Please
                    contact our support team to learn more and get started with a plan that suits your farm.
                  </p>
                  <button className="register-btn" onClick={() => navigate("/contact")}>
                    📞 Contact Support
                  </button>
                </>
              )}
            </div>
          )}

          {!loading && !plotsLoading && !error && (
            <div style={{ marginTop: "25px" }}>
              <h2 className="welcome-title">Maintenance History</h2>

              {historyLoading && <p>Loading history…</p>}
              {!historyLoading && history.length === 0 && <p>No maintenance plan changes recorded yet.</p>}

              {history.map((h) => (
                <div key={h.id} style={{ marginBottom: "12px", paddingBottom: "12px", borderBottom: "1px solid #eee" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <strong>{h.maintenance_type_name || "-"}</strong>
                    <span style={{ fontSize: "12px", color: "#888" }}>{new Date(h.created_at).toLocaleString()}</span>
                  </div>
                  {h.maintenance_status_name && (
                    <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#555" }}>Status: {h.maintenance_status_name}</p>
                  )}
                  {formatDate(h.maintenance_validity_date) && (
                    <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#555" }}>Valid until: {formatDate(h.maintenance_validity_date)}</p>
                  )}
                  {formatCurrency(h.maintenance_due_amount) && (
                    <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#555" }}>Due: {formatCurrency(h.maintenance_due_amount)}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {!loading && !plotsLoading && !error && (
            <div style={{ marginTop: "25px" }}>
              <h2 className="welcome-title">Maintenance Reports</h2>

              {reportsError && <p className="login-error" style={{ color: "red" }}>{reportsError}</p>}
              {reportsLoading && <p>Loading reports…</p>}
              {!reportsLoading && reports.length === 0 && !reportsError && (
                <p>No maintenance reports yet for your plot.</p>
              )}

              <div className="dashboard-grid">
                {reports.map((report) => (
                  <div className="card" key={report.id}>
                    <h3>{formatDate(report.reporting_datetime) || new Date(report.reporting_datetime).toLocaleDateString()}</h3>
                    <p>👷 {report.supervisor_name}</p>
                    <p>🌱 No. of Plants: {report.actual_healthy_plants_count}</p>
                    <p>📸 {(report.photos || []).length} Photo(s)</p>
                    <button
                      className="login-btn"
                      style={{ width: "auto", padding: "8px 16px" }}
                      onClick={() => setSelectedReport(report)}
                    >
                      View Report
                    </button>
                  </div>
                ))}
              </div>

              {selectedReport && (
                <div className="section-card" style={{ marginTop: "20px" }} ref={detailsRef}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3>Report Details — {formatDate(selectedReport.reporting_datetime)}</h3>
                    <button
                      className="register-btn"
                      style={{ width: "auto", padding: "6px 14px" }}
                      onClick={() => setSelectedReport(null)}
                    >
                      Close
                    </button>
                  </div>

                  <p><strong>Plot Number:</strong> {selectedReport.plot_number}</p>
                  <p><strong>Supervisor:</strong> {selectedReport.supervisor_name}</p>
                  <p><strong>Reporting Date/Time:</strong> {new Date(selectedReport.reporting_datetime).toLocaleString()}</p>
                  <p><strong>Added Location:</strong> {selectedReport.latitude != null && selectedReport.longitude != null
                    ? (
                      <a
                        href={`https://maps.google.com/?q=${selectedReport.latitude},${selectedReport.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {selectedReport.latitude}, {selectedReport.longitude}
                      </a>
                    )
                    : "—"}
                  </p>

                  <h4>Plot Maintenance Checklist</h4>
                  {CHECKLIST_ITEMS.map((item) => (
                    <p key={item.key}>{item.label}: <strong>{selectedReport[item.key] || "—"}</strong></p>
                  ))}

                  <h4>Yield Report</h4>
                  <p><strong>When to collect:</strong> {selectedReport.when_to_collect_yield || "—"}</p>
                  {selectedReport.yield_types?.length > 0 && (
                    <p><strong>Yield types:</strong> {selectedReport.yield_types.join(", ")}</p>
                  )}
                  <p><strong>Remarks:</strong> {selectedReport.yield_remarks || "—"}</p>

                  <h4>Photos</h4>
                  {(selectedReport.photos || []).length === 0 && <p>No photos attached.</p>}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    {(selectedReport.photos || []).map((photo) => (
                      <a href={photo.file_url} target="_blank" rel="noopener noreferrer" key={photo.id || photo.file_url}>
                        <img
                          src={photo.file_url}
                          alt={photo.slot}
                          style={{ width: "120px", height: "120px", objectFit: "cover", borderRadius: "6px" }}
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </>
  );
}

export default CustomerMaintenance;
